import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useRouteMatch, useParams } from "react-router-dom"
import { Table, Skeleton, Popconfirm, notification, Tooltip, Dropdown, Menu, Button, Checkbox, Tag } from "antd"
import { DownloadOutlined, PlusOutlined, UploadOutlined, FilePdfOutlined, FileZipOutlined, FileTextOutlined, EditOutlined, DeleteOutlined, DownOutlined, FolderOutlined, LoadingOutlined, SearchOutlined, FilterOutlined, CloseOutlined } from "@ant-design/icons"
import moment from "moment"

import {
    fetchAccountingBatches,
    selectAccountingBatches,
    deleteAccountingBatch,
    selectTotal,
    selectPagination,
    selectLoading,
    generateAccountingBatchCsv,
    generateAccountingBatchUploadFile,
    generateAccountingBatchInvoicePdf,
    generateAccountingBatchBillImagesZip,
    filterAccountingBatches,
    selectFilters,
    selectSort,
    setFilters,
    setSort,
    setBulkEditBatchIds,
    editBatches,
    selectBulkEditBatchIds,
} from "../accountingBatchesSlice"

import Role from "../../../userRoles/Role"
import {formatCurrency} from "../../../../lib/utilities";
import useConfirmationModal from "../../../alertsPanel/ShowConfirm"
import ColumnDateFilter from "../../../../components/utilityComponents/table/ColumnDateFilter"
import { tableFiltersApplied } from "../../../../functions/objectFunctions/tableFiltersApplied"
import { allObjectValuesEmpty } from "../../../../functions/objectFunctions/allObjectValuesEmpty"
import ColumnSearchFilter from "../../../../components/utilityComponents/table/ColumnSearchFilter"
import ColumnHeaderTitle from "../../../../components/utilityComponents/table/ColumnHeaderTitle"
import { selectRoles } from "../../../userRoles/userRolesSlice"

export default function AccountingBatchTable({ scopedByClient }) {
    const { url } = useRouteMatch()
    const { clientId } = useParams()
    const dispatch = useDispatch()
    const accountingBatches = useSelector(selectAccountingBatches)
    // console.log(accountingBatches)
    const total = useSelector(selectTotal)
    const pagination = useSelector(selectPagination)
    const loading = useSelector(selectLoading)
    const roles = useSelector(selectRoles)
    const filters = useSelector(selectFilters)
    const sort = useSelector(selectSort)
    const bulkEditBatchIds = useSelector(selectBulkEditBatchIds)
    const [loaded, setLoaded] = useState(false)
    // const [filters, setFilters] = useState({})
    // const [sort, setSort] = useState({
    //     column: "created_at",
    //     direction: "desc",
    // })
    useEffect(() => {
        handleFetchInitialAccountingBatches()
        dispatch(setFilters({}))
        dispatch(setSort({ column: "batch_date", direction: "desc", secondaryColumn: "created_at", secondaryDirection: "desc" }))
    }, [url])

    useEffect(() => {
        if (!loaded) {
            setLoaded(true)
            handleFetchInitialAccountingBatches(true)
        }
        // eslint-disable-next-line
    }, [loaded, url, dispatch, pagination])


    // const handleFetchInitialAccountingBatches = () => {
    //     dispatch(fetchAccountingBatches(clientId, pagination))
    // }

    const handleFetchInitialAccountingBatches = (withFilters = false) => {
        if (!withFilters) {
            dispatch(fetchAccountingBatches(clientId, pagination))
        } else {
            dispatch(filterAccountingBatches({ clientId: clientId, filters: filters, pagination: pagination, sort: sort }))
        }
        // console.log(clientId, pagination, sort, billableAccountId, supplyAccountId, groupAccountId)
    }

    const handleTableChange = (newPagination, filters, sorter) => {
        if (allObjectValuesEmpty(filters) && allObjectValuesEmpty(sorter)) {
            dispatch(fetchAccountingBatches(clientId, newPagination))
        } else {
        //     dispatch(fetchAccountingBatches(clientId, newPagination))
            handleFilterAccountingBatches(newPagination)
        }

    }

    const handleFilterAccountingBatches = (newPagination = null, newSort = null) => {
        return dispatch(
            filterAccountingBatches({
                clientId: clientId,
                filters: filters,
                pagination: !!newPagination ? newPagination : pagination,
                sort: !!newSort ? newSort : sort,
            })
        )
    }

    const handleSortAccountingBatchesChange = (column) => () => {
        let newDirection = sort.direction === "asc" ? "desc" : "asc"
        let newSort = {
            column: column,
            direction: newDirection,
            secondaryColumn: "created_at",
            secondaryDirection: "desc",
        }
        dispatch(setSort(newSort))
        handleFilterAccountingBatches(null, newSort)
    }
   

    const deleteRecord = async (id) => {
        const response = await dispatch(deleteAccountingBatch(id))
        if (response.success) {
            notification.open({
                message: "Success",
                description: "Deleted Client Batch",
                duration: 0.8,
            })
        } else {
            notification.open({
                message: "Error",
                description: "There was an error deleting the Client Batch",
                duration: 0.8,
            })
        }
    }

    const accountingBatchesCsv = () => {
        notification.open({
            message: "Client batch export started",
            duration: 0.8,
        })

        dispatch(generateAccountingBatchCsv(clientId))
    }

    const generateUploadFile = async (accountingBatchId) => {
        notification.open({
            message: "Upload file generation started",
            duration: 0.8,
        })

        dispatch(generateAccountingBatchUploadFile(accountingBatchId, clientId))
    }

    const showConfirm = useConfirmationModal();
    const handleDeleteClick = (id) => {
        showConfirm(deleteAccountingBatch, id);
    };

    // A/R feedback item 2: the file was built from data that has since changed. MUC roles only
    // (`update`) -- clients just see the download icon. Regenerated from the row's menu.
    const renderStale = (record, kind) =>
        record.stale_files && record.stale_files[kind] ? (
            <Role action="update" model="accounting_batch">
                <Tooltip title="Changed since this file was generated. Regenerate it from the menu.">
                    <Tag color="red" style={{ marginLeft: "6px", marginRight: 0 }}>
                        Stale
                    </Tag>
                </Tooltip>
            </Role>
        ) : null

    const generateInvoicePdf = async (accountingBatchId) => {
        notification.open({
            message: "Invoice PDF generation started",
            duration: 0.8,
        })

        dispatch(generateAccountingBatchInvoicePdf(accountingBatchId, clientId))
    }

    const generateBillImagesZip = async (accountingBatchId) => {
        notification.open({
            message: "Zipping bill images -- this can take a few minutes on a large batch",
            duration: 0.8,
        })

        dispatch(generateAccountingBatchBillImagesZip(accountingBatchId, clientId))
    }

    const renderSearchOutlinedButton = (filterKey) => {
        return <SearchOutlined style={{ fontSize: "16px", color: tableFiltersApplied(filterKey, filters) ? "#1890ff" : undefined }} />
    }

    const renderColumnHeaderTitle = (title, altTitle = null, columnClass = '') => {
        return <ColumnHeaderTitle title={title} altTitle={altTitle} handleSortChange={handleSortAccountingBatchesChange} sort={sort} columnClass={columnClass}  />
    }

    const renderResetFilters = () => {
        if (allObjectValuesEmpty(filters) && (allObjectValuesEmpty(sort) || sort.column === "batch_date")) {
            return null
        }
        return (
            <div className={"actions-alternate-buttons"}>
                <a
                    href="#"
                    onClick={() => {
                        handleFetchInitialAccountingBatches()
                        dispatch(setFilters({}))
                        dispatch(setSort({ column: "batch_date", direction: "desc", secondaryColumn: "created_at", secondaryDirection: "desc" }))
                    }}>
                    <FilterOutlined />
                    Reset Filters
                </a>
            </div>
        )
    }

    // function checkBulkEditIds() {
    //     if (accountingBatches.length == 0 || bulkEditBatchIds.length == 0) return false
    //     // for (let i = 0; i < accountingBatches.length; i++) {
    //     //     if (accountingBatches[i].closed || bulkEditBatchIds.includes(accountingBatches[i].id)) continue
    //     //     else {
    //     //         return false
    //     //     }
    //     // }
    //     // return true
    // }

    function checkBulkEditIds() {
        if (accountingBatches.length === 0 || bulkEditBatchIds.length === 0) {
            return false
        }

        const bulkEditIdSet = new Set(bulkEditBatchIds.map((batch) => batch.id))

        return accountingBatches.every((batch) => bulkEditIdSet.has(batch.id))
    }

    const handleBulkEdit = async () => {
        await dispatch(
            editBatches(bulkEditBatchIds, {
                clientId: clientId,
                filters: filters,
                pagination: pagination,
                sort: sort,
            })
        )
    }

    // const handleChecked = (closed, id) => {
    //     if(!roles.includes("super_admin") && closed){
    //         return true
    //     }
    //     for(let i = 0; i < bulkEditBatchIds.length; i++){
    //         if(bulkEditBatchIds[i].id == id) return true 
    //     }
    //     return false
    // }

    const bulkEditBatchIdSet = new Set(bulkEditBatchIds.map((batch) => batch.id))

    const handleChecked = (closed, id) => {
        if (!roles.includes("super_admin") && closed) {
            return true
        }

        return bulkEditBatchIdSet.has(id)
    }

    const countBatches = () => {
        let accsToOpen = 0
        let accsToClose = 0 
        bulkEditBatchIds.map((batch, i) => {
            if(batch.closed) accsToClose += 1;
            if(!batch.closed) accsToOpen += 1;
        })

        return { opening: accsToOpen, closing: accsToClose }
    }

    return (
        <div className="muc-table card">
            <div className="card-body">
                <div>
                    <div className="card-body-heading">
                        <div>
                            <div className="badged-header">
                                <h2>Client Batches</h2>
                                <div className="badge">{total}</div>
                            </div>
                            {bulkEditBatchIds.length > 0 && roles.includes("super_admin") && (
                                <>
                                    <div className="badged-header" style={{ gap: 10, color: "white" }}>
                                        <h5>- Batches to be opened:</h5>
                                        <div className="global-batch-filter-label-text">{countBatches().opening}</div>
                                    </div>
                                    <div className="badged-header" style={{ gap: 10, color: "white" }}>
                                        <h5>- Batches to be closed:</h5>
                                        <div className="global-batch-filter-label-text">{countBatches().closing}</div>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="actions">
                            <div className="table-action-links">
                                {bulkEditBatchIds.length > 0 && (
                                    <Role action="create" model="accounting_batch">
                                        <a href="javascript:;" onClick={() => handleBulkEdit()}>
                                            <CloseOutlined />
                                            {roles.includes("super_admin") ? "Handle" : "Close"} Batches
                                        </a>
                                    </Role>
                                )}
                                {renderResetFilters()}
                                <Role action="create" model="accounting_batch">
                                    <Link to={`${url}/new`}>
                                        <PlusOutlined />
                                        Add New
                                    </Link>
                                </Role>

                                {/* <a href="javascript:;" onClick={() => console.log("Import")}>
                                    <UploadOutlined />
                                    Import
                                </a> */}

                                <a href="javascript:;" onClick={() => accountingBatchesCsv()}>
                                    <DownloadOutlined />
                                    Export
                                </a>
                            </div>
                        </div>
                    </div>

                    <Skeleton active loading={loading}>
                        <Table dataSource={accountingBatches} pagination={{ ...pagination, showSizeChanger: true, total: total }} onChange={handleTableChange} rowKey={(r) => r.id}>
                            {(roles.includes("super_admin") || roles.includes("muc_admin")) && (
                                <Table.Column
                                    key="in"
                                    dataIndex="in"
                                    render={(text, record, index) => {
                                        // console.log(text, record, index)
                                        return (
                                            <Checkbox
                                                disabled={record.closed && !roles.includes("super_admin")}
                                                checked={handleChecked(record.closed, record.id)}
                                                onClick={(e) => {
                                                    // make into object
                                                    if (e.target.checked) {
                                                        dispatch(setBulkEditBatchIds([...bulkEditBatchIds, { id: record.id, closed: !record.closed }]))
                                                    } else {
                                                        dispatch(setBulkEditBatchIds(bulkEditBatchIds.filter((batch, i) => batch.id !== record.id)))
                                                    }
                                                }}
                                            />
                                        )
                                    }}
                                    title={
                                        <Checkbox
                                            checked={checkBulkEditIds()}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    const bulkEditIdSet = new Set(bulkEditBatchIds.map((batch) => batch.id))
                                                    const newBatches = accountingBatches
                                                        .filter((batch) => {
                                                            if (bulkEditIdSet.has(batch.id)) return false
                                                            if (roles.includes("super_admin")) return true
                                                            return !batch.closed
                                                        })
                                                        .map((batch) => ({ id: batch.id, closed: !batch.closed }))

                                                    dispatch(setBulkEditBatchIds([...bulkEditBatchIds, ...newBatches]))
                                                } else {
                                                    dispatch(setBulkEditBatchIds([]))
                                                }
                                            }}
                                        />
                                    }
                                />
                            )}
                            <Table.Column title="Batch Closed" key="closed" dataIndex="closed" render={(text, record) => <>{record.closed ? "Closed" : ""}</>} />
                            <Table.Column
                                title={renderColumnHeaderTitle("batch_group_name")}
                                key="batch_group_name"
                                dataIndex="batch_group_name"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialAccountingBatches}
                                        filterAction={filterAccountingBatches}
                                        filterColumn={"batch_group_name"}
                                        filterType={"batch_group_name_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("batch_group_name_cont")}
                                render={(text, record) => <>{record.batch_group?.name}</>}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("batch_code")}
                                key="batch_code"
                                dataIndex="batch_code"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialAccountingBatches}
                                        filterAction={filterAccountingBatches}
                                        filterColumn={"batch_code"}
                                        filterType={"batch_code_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("batch_code_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("batch_date")}
                                key="batch_date"
                                dataIndex="batch_date"
                                filterDropdown={({ visible }) => (
                                    <ColumnDateFilter
                                        handleFetchInitial={handleFetchInitialAccountingBatches}
                                        filterAction={filterAccountingBatches}
                                        filterColumn={"batch_date"}
                                        filterType={"batch_date"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("batch_date_lteq" || "batch_date_gteq")}
                                render={(text, record) => <>{moment(record.batch_date).format("MM/DD/YYYY")}</>}
                            />
                            {/* TODO: Make money formatting a library function */}
                            <Table.Column
                                title={renderColumnHeaderTitle("bills_total")}
                                key="bills_total"
                                dataIndex="bills_total"
                                align="right"
                                render={(text, record, index) => formatCurrency(Math.round(record.bills_total * 100) / 100)}
                            />
                            <Table.Column title={renderColumnHeaderTitle("line_item_count")} key="line_item_count" dataIndex="line_item_count" />
                            {/* {!roles.includes("client_user") && ( */}
                            <Table.Column
                                title="Upload File"
                                key="download"
                                // LoadingOutlined icon when new file is generating
                                render={(text, record) => {
                                    if (!record.upload_file_url) {
                                        return null
                                    }

                                    if (record.generating_file) {
                                        return <LoadingOutlined />
                                    } else {
                                        return (
                                            <>
                                                <a href={record.upload_file_url} target="_blank" rel="noreferrer">
                                                    <Tooltip title="View Report">
                                                        <FileTextOutlined />
                                                    </Tooltip>
                                                </a>
                                                {renderStale(record, "upload_file")}
                                            </>
                                        )
                                    }
                                }}
                            />
                            {/* The stored invoice PDF -- visible to every role that can read the batch
                                (client admin, client user, MUC admin). The spinner is checked FIRST:
                                Upload File above checks the URL first, so its spinner never shows the
                                first time a file is generated. */}
                            <Table.Column
                                title="Invoice"
                                key="invoice_pdf"
                                render={(text, record) => {
                                    if (record.generating_invoice_pdf) return <LoadingOutlined />
                                    if (!record.invoice_pdf_url) return null
                                    return (
                                        <>
                                            <a href={record.invoice_pdf_url} target="_blank" rel="noreferrer">
                                                <Tooltip title="Download Invoice">
                                                    <FilePdfOutlined />
                                                </Tooltip>
                                            </a>
                                            {renderStale(record, "invoice_pdf")}
                                        </>
                                    )
                                }}
                            />
                            {/* Every bill image in the batch, zipped, with a contents.txt. Same roles
                                and spinner-first order as Invoice. */}
                            <Table.Column
                                title="Bills"
                                key="bill_images_zip"
                                render={(text, record) => {
                                    if (record.generating_bill_images_zip) return <LoadingOutlined />
                                    if (!record.bill_images_zip_url) return null
                                    return (
                                        <>
                                            <a href={record.bill_images_zip_url} target="_blank" rel="noreferrer">
                                                <Tooltip title="Download bill images">
                                                    <FileZipOutlined />
                                                </Tooltip>
                                            </a>
                                            {renderStale(record, "bill_images_zip")}
                                        </>
                                    )
                                }}
                            />
                            {/* )} */}
                            {/* {roles.includes("super_admin") && (
                                <Table.Column
                                    title={renderColumnHeaderTitle("created_at")}
                                    key="created_at"
                                    dataIndex="created_at"
                                    // filterDropdown={({ visible }) => (
                                    //     <ColumnDateFilter
                                    //         handleFetchInitial={handleFetchInitialBills}
                                    //         filterAction={filterBills}
                                    //         filterColumn={"created_at"}
                                    //         filterType={"created_at"}
                                    //         filters={filters}
                                    //         pagination={pagination}
                                    //         setFilters={setFilters}
                                    //         sort={sort}
                                    //         setSort={setSort}
                                    //         visible={visible}
                                    //     />
                                    // )}
                                    // filterIcon={() => renderSearchOutlinedButton("created_at_lteq" || "created_at_gteq")}
                                    render={(text, record, index) => moment(text).format("MM/DD/YY - h:mm:ss A")}
                                />
                            )} */}
                            <Table.Column
                                title="Actions"
                                key="action"
                                render={(text, record) => (
                                    <>
                                        <Dropdown
                                            overlay={
                                                <Menu>
                                                    <Role action="update" model="accounting_batch">
                                                        <Menu.Item key="1" icon={<FolderOutlined />}>
                                                            <Link to={`${url}/${record.id}/bills`}>Bills</Link>
                                                        </Menu.Item>
                                                    </Role>

                                                    <Role action="update" model="accounting_batch">
                                                        <Menu.Item key="2" icon={<EditOutlined />}>
                                                            <Link to={`${url}/${record.id}/edit`}>Edit Client Batch</Link>
                                                        </Menu.Item>
                                                    </Role>

                                                    <Role action="read" model="accounting_batch">
                                                        <Menu.Item key="3" icon={<FileTextOutlined />}>
                                                            <a href="javascript:;" onClick={() => generateUploadFile(record.id)}>
                                                                Generate Upload File
                                                            </a>
                                                        </Menu.Item>
                                                    </Role>

                                                    {/* `update`, not `read`: only MUC admins and above can
                                                        generate_invoice_pdf, and client admins and users,
                                                        who can read, would just get a refusal. They download
                                                        from the Invoice column instead. */}
                                                    <Role action="update" model="accounting_batch">
                                                        <Menu.Item key="4" icon={<FilePdfOutlined />}>
                                                            <a href="javascript:;" onClick={() => generateInvoicePdf(record.id)}>
                                                                Invoice (PDF)
                                                            </a>
                                                        </Menu.Item>
                                                    </Role>

                                                    {/* Same gate as Invoice (PDF): MUC roles generate,
                                                        everyone who can read downloads from the Bills column. */}
                                                    <Role action="update" model="accounting_batch">
                                                        <Menu.Item key="6" icon={<FileZipOutlined />}>
                                                            <a href="javascript:;" onClick={() => generateBillImagesZip(record.id)}>
                                                                Bill images (zip)
                                                            </a>
                                                        </Menu.Item>
                                                    </Role>

                                                    <Role action="destroy" model="accounting_batch">
                                                        <Menu.Item key="5" icon={<DeleteOutlined />} onClick={() => handleDeleteClick(record.id)} disabled={record.has_been_closed}>
                                                            Delete Client Batch
                                                        </Menu.Item>
                                                    </Role>
                                                </Menu>
                                            }>
                                            <Button>
                                                Actions <DownOutlined />
                                            </Button>
                                        </Dropdown>
                                    </>
                                )}
                            />
                        </Table>
                    </Skeleton>
                </div>
            </div>
            {/* <div className="card-footer">
                <div className="card-footer-left"></div>
                <div className="card-footer-right pagination"></div>
            </div> */}
        </div>
    )
}
